/**
 * weekLanes — side-by-side placement for occurrences that overlap in time.
 *
 * THE BUG THIS EXISTS TO FIX. The week grid positions every card absolutely at
 * `left: 5, right: 5` — full column width — with no collision handling at all.
 * Two lessons at the same clock time in different locations (a Lakeville 4:30
 * and an HLDC 4:30, which is the normal shape of a CYV weeknight) therefore
 * painted directly on top of each other. The later one won and the earlier one
 * was INVISIBLE — not crowded, not clipped, absent. A staff member reading the
 * week saw a confident, wrong schedule with no hint anything was missing, which
 * is the worst failure mode a calendar has. Reported by Anthony 2026-08-30.
 *
 * WHY A SEPARATE MODULE. The placement maths is the part that is actually right
 * or wrong here, and it is worth testing without mounting CalendarTab — a
 * component that also does bulk edits, cancels and Firestore writes. Same
 * reasoning as `slotColumns.js` beside it. Nothing in this file touches React,
 * the DOM or Firestore.
 *
 * THE ALGORITHM, and the one subtlety in it. Events are swept in start order
 * and packed into the first lane whose previous occupant has already ended.
 * The subtlety is that lane COUNT is per-CLUSTER, not per-day: a cluster is a
 * maximal run of occurrences connected by overlap, and it closes as soon as one
 * starts at or after the furthest end seen so far. Widths are computed per
 * cluster, so a single 4:30 collision does not squeeze the other six lessons of
 * an otherwise clean evening down to half width. Sharing one count across the
 * whole day is the obvious implementation and it is visibly wrong.
 *
 * DELIBERATELY NOT HANDLED: the grid gives every card a 30px MINIMUM height
 * (~35 minutes at the current scale), so two genuinely back-to-back 30-minute
 * lessons still kiss by ~5px. Laning on rendered height rather than real time
 * would fix that, at the cost of putting every back-to-back pair into half-width
 * columns — a large, permanent cost for a 5px cosmetic seam. Lanes key on TIME
 * overlap, which is what "these are happening at once" actually means.
 */

// "HH:MM" → minutes past midnight. Shared with CalendarTab so the grid's
// vertical placement and this horizontal placement can never disagree about
// when something starts.
export const toMinutes = (t) => {
	const [h, m] = String(t || '').split(':').map(Number);
	return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/**
 * Assign each occurrence a lane and the lane count of its cluster.
 *
 * @param {Array} occs occurrences in any order; each needs startTime/endTime
 *                     as "HH:MM". Order is NOT mutated — the return is keyed by
 *                     the caller's original index, because the caller renders
 *                     from its own array and a reordered result would silently
 *                     mis-key React children.
 * @returns {Map<number, {lane: number, lanes: number}>}
 */
export function assignLanes(occs) {
	const placed = new Map();
	if (!Array.isArray(occs) || occs.length === 0) return placed;

	const items = [];
	occs.forEach((s, i) => {
		const start = toMinutes(s && s.startTime);
		const end = toMinutes(s && s.endTime);
		// An occurrence with no usable time cannot collide with anything —
		// give it a full-width lane rather than dropping it or crashing.
		if (start === null) {
			placed.set(i, { lane: 0, lanes: 1 });
			return;
		}
		items.push({ i, start, end: end !== null && end > start ? end : start + 1 });
	});

	// Longest-first within a start time keeps the long lesson in lane 0, so the
	// short things that collide with it stack to its right rather than pushing
	// it around. Index last, so the result is stable for equal times.
	items.sort((a, b) => a.start - b.start || b.end - a.end || a.i - b.i);

	let cluster = [];
	let clusterEnd = -Infinity;

	const flush = () => {
		if (cluster.length === 0) return;
		const laneEnds = []; // laneEnds[n] = when lane n became free
		for (const it of cluster) {
			// `<=` not `<`: a lesson ending at 4:30 and one starting at 4:30 do
			// not overlap, and forcing them apart would halve the width of every
			// back-to-back pair in the studio.
			let lane = laneEnds.findIndex((end) => end <= it.start);
			if (lane === -1) {
				lane = laneEnds.length;
				laneEnds.push(it.end);
			} else {
				laneEnds[lane] = it.end;
			}
			it.lane = lane;
		}
		for (const it of cluster) placed.set(it.i, { lane: it.lane, lanes: laneEnds.length });
		cluster = [];
		clusterEnd = -Infinity;
	};

	for (const it of items) {
		if (it.start >= clusterEnd) flush(); // nothing before this can still be running
		cluster.push(it);
		clusterEnd = Math.max(clusterEnd, it.end);
	}
	flush();

	return placed;
}
